import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect } from 'react';
import { Trans } from 'react-i18next';
import styled from 'styled-components';
import mediaQueries from 'syf-component-library/const/mediaQueries';
import { fontSizes, spacing } from 'syf-component-library/defaultTheme';
import Button from 'syf-component-library/ui/atoms/Button/Button';
import MessageBox from 'syf-component-library/ui/patterns/MessageBox/MessageBox';
import { ModalFooter } from 'syf-component-library/ui/patterns/SlideInModal';
import { FooterContainer } from 'syf-component-library/ui/patterns/SlideInModal/subcomponents';
import { Inset } from 'syf-component-library/ui/spacing';
import { Small } from 'syf-component-library/ui/typography';
import { formatDateStringToObject, formatDisplayDate, formatDisplayDay } from 'syf-js-utilities/formats';
import formatDisplayAccount from 'syf-js-utilities/formats/formatDisplayAccount';
import { usePaymentAmountAtom } from '../../../../store/paymentAtoms';
import { AnalyticsEventFeatures } from '../../../../typings/analytics';
import { MAPFlowTypes } from '../../../../typings/deepLink';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE } from '../../../../const/analytics';
import { autopayConfirmationImg } from '../../../../const/assets';
import { FAR_FILE_ALT } from '../../../../const/iconProps';
import { paymentOptions } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import isDCHSettlementEnrolled from '../../../../helpers/isDCHSettlementEnrolled';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import useFormValues from '../../../../hooks/machines/useFormValues';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import useAnalyticsEventV2Creators from '../../../../hooks/useAnalyticsEventV2Creators';
import { submitSuccessAnalyticsHandler } from '../../../../machines/oneTimePayment/helpers';
import i18n, { t } from '../../../../strings/i18n';
import Disclaimer from '../../../atoms/Disclaimer';
import { PaymentDetailsContentRow, PaymentDetailsHeader } from '../../PaymentsDetailsGrid';
import { getPaymentAmountDisplay } from '../helpers';
import PaymentStepper from '../PaymentFormSubcomponents/PaymentStepper';
import { Card, OneTimePaymentConfirmationDetailsWrapper } from '../subcomponents';
import OneClickAutopayErrorBanner from './OneClickAutopayErrorBanner';
import OneTimePaymentConfirmationDetails from './OneTimePaymentConfirmationDetails';
const AutopayCard = styled(Card) `
  background-color: transparent;
`;
const ManageAutopayButton = styled(Button) `
  display: inline;
  margin: 0;
  padding: 0;
  font-size: ${fontSizes.small}px;
`;
const AutopayDetailsWrapper = styled(Inset) `
  padding: 0px ${spacing.large}px ${spacing.base}px ${spacing.large}px;

  @media ${mediaQueries.lessThanSmall} {
    padding: ${spacing.small}px 0px ${spacing.base}px 0px;
  }
`;
const getAutopayPaymentAmount = ({ paymentOption, paymentAmount }) => {
    if (paymentOption === paymentOptions.TOTAL_AMOUNT_DUE) {
        return t(`oneClickAutopayPaymentOptions.${paymentOptions.MONTHLY_PAYMENT}`);
    }
    if (paymentOption === paymentOptions.OTHER_PAYMENT ||
        paymentOption === paymentOptions.FIXED_PAYMENT) {
        return getPaymentAmountDisplay(paymentOption, paymentAmount);
    }
    return t(`oneClickAutopayPaymentOptions.${paymentOption}`);
};
const getPrimaryReason = (isEnrolledInOneClickAutopay, isPaymentPending, hasOneClickAutopayError) => {
    if (isPaymentPending) {
        if (isEnrolledInOneClickAutopay) {
            if (hasOneClickAutopayError) {
                return DATA_REASON.CLOSE.AUTOPAY.PENDING_FAILED;
            }
            return DATA_REASON.CLOSE.AUTOPAY.PENDING_SUCCESS;
        }
        return DATA_REASON.CLOSE.ONE_TIME.PENDING;
    }
    if (isEnrolledInOneClickAutopay) {
        if (hasOneClickAutopayError) {
            return DATA_REASON.CLOSE.AUTOPAY.FAILED;
        }
        return DATA_REASON.CLOSE.AUTOPAY.SUCCESS;
    }
    return DATA_REASON.CLOSE.ONE_TIME.SUCCESS;
};
const OneClickAutopayDetails = ({ paymentOption, paymentAmount, nextDueDate, payFrom }) => {
    const { sendToMainMachine } = useContext(MainContext);
    const handleOnClick = () => sendToMainMachine({
        type: 'SET_AUTOPAY_FLOW',
        isPaymentTypeVisible: false
    });
    const oneClickAutopayNextDate = nextDueDate
        ? formatDisplayDate(nextDueDate)
        : t('autopay.noNextDueDate');
    return (_jsxs(AutopayDetailsWrapper, { children: [_jsxs(MessageBox, { children: [_jsx(PaymentDetailsHeader, { dataCy: "autopay-details-header-text", text: "label.autopayDetails" }), _jsxs(AutopayCard, Object.assign({ weight: "bold" }, { children: [_jsx(PaymentDetailsContentRow, { dataCyForLabel: "one-click-autopay-payment-amount-label", dataCyForValue: "one-click-autopay-payment-amount-value", label: "label.paymentAmount", value: getAutopayPaymentAmount({
                                    paymentOption,
                                    paymentAmount
                                }) }), _jsx(PaymentDetailsContentRow, { dataCyForLabel: "one-click-autopay-payment-next-date-label", dataCyForValue: "one-click-autopay-payment-next-date-value", label: "autopay.nextDate", value: oneClickAutopayNextDate }), nextDueDate && (_jsx(PaymentDetailsContentRow, { dataCyForLabel: "one-click-autopay-payment-monthly-date-label", dataCyForValue: "one-click-autopay-payment-monthly-date-value", label: "autopay.autopayDate", value: t('autopay.eachMonth', {
                                    day: formatDisplayDay(nextDueDate)
                                }) })), _jsx(PaymentDetailsContentRow, { dataCyForLabel: "one-click-autopay-payment-method-label", dataCyForValue: "one-click-autopay-payment-method-value", label: "makePayment.paymentMethod", value: payFrom }), _jsx(Inset, Object.assign({ top: "small" }, { children: _jsx(Small, Object.assign({ "data-cy": "manage-autopay-help-text" }, { children: _jsx(Trans, { components: [
                                            _jsx(ManageAutopayButton, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-object": "link", "data-reason": "manage autopay onetime-autopay success", "data-type": DATA_TYPE.MAKE_A_PAYMENT, onClick: handleOnClick }, { children: t('button.manageAutopay') }), "autopay-button")
                                        ], i18n: i18n, i18nKey: "autopay.oneClickManageAutopay" }) })) }))] }))] }), _jsx(Inset, Object.assign({ top: "xlarge" }, { children: _jsx(Disclaimer, { dataCy: "payment-disclaimer", headingText: t('autopay.paymentAmountDetails'), iconFAText: FAR_FILE_ALT, paragraph: t('autopay.paymentAmountDescription') }) }))] }));
};
const OneClickAutopay = ({ hasOneClickAutopayError, paymentOption, paymentAmount, nextDueDate, payFrom }) => {
    if (hasOneClickAutopayError) {
        return _jsx(OneClickAutopayErrorBanner, { isOneTimeSuccess: true });
    }
    return (_jsx(OneClickAutopayDetails, { nextDueDate: nextDueDate, payFrom: payFrom, paymentAmount: paymentAmount, paymentOption: paymentOption }));
};
const OneTimePaymentConfirmation = () => {
    var _a, _b, _c;
    const { staticHostName, featureFlags, triggerAnalyticsEvent } = useContext(MainContext);
    const [apiMachine] = useApiMachine();
    const oneTimePaymentForm = useFormValues();
    const [, oneTimePaymentActor] = usePaymentFlowMachine();
    const { sendToMainMachine, additionalProps, handleClose } = useContext(MainContext);
    const [paymentAmountValue] = usePaymentAmountAtom();
    const { createConfirmationEventObject } = useAnalyticsEventV2Creators(AnalyticsEventFeatures.MAKE_A_PAYMENT);
    const { accountSummaryQueryResult, autopayStatusQueryResult, paymentMethodsQueryResult, alternatePaymentMethodsQueryResult } = apiMachine.context;
    const { paymentResult } = oneTimePaymentActor.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: paymentMethodData } = paymentMethodsQueryResult;
    const { data: autopayStatus } = autopayStatusQueryResult;
    const { data: alternatePaymentMethodData } = alternatePaymentMethodsQueryResult;
    const { billing } = accountSummary;
    const productType = accountSummary.product.type;
    const nextDueDate = (_a = autopayStatus === null || autopayStatus === void 0 ? void 0 : autopayStatus.enrollmentDetails) === null || _a === void 0 ? void 0 : _a.nextDueDate;
    const creditLimit = Number((_b = accountSummary === null || accountSummary === void 0 ? void 0 : accountSummary.credit) === null || _b === void 0 ? void 0 : _b.creditLimit);
    const availableCredit = Number((_c = accountSummary === null || accountSummary === void 0 ? void 0 : accountSummary.credit) === null || _c === void 0 ? void 0 : _c.availableCredit);
    const paymentMethods = [
        ...paymentMethodData.paymentMethods,
        ...alternatePaymentMethodData.paymentMethods
    ];
    const { hasOneClickAutopayError, initialAvailableCredit, isPaymentPending, paymentId, shouldDisplayAvailableCredit, shouldHaveEnrolledInOneClickAutopay } = paymentResult;
    const { paymentOption, paymentDate } = oneTimePaymentForm;
    const paymentMethod = paymentMethods === null || paymentMethods === void 0 ? void 0 : paymentMethods.find((method) => {
        return `${method.id}` === (oneTimePaymentForm === null || oneTimePaymentForm === void 0 ? void 0 : oneTimePaymentForm.paymentMethod);
    });
    const { paymentMedium } = paymentMethod;
    const payFrom = formatDisplayAccount(paymentMethod);
    const paymentAmount = paymentOption === paymentOptions.OTHER_PAYMENT
        ? oneTimePaymentForm.paymentAmount
        : paymentAmountValue;
    const paymentDateString = paymentDate;
    const scheduledPaymentDate = formatDateStringToObject(paymentDateString);
    const shouldShowMakeAnotherPaymentButton = isDCHSettlementEnrolled(accountSummary.billing, productType, featureFlags);
    const handleMakeAnotherPayment = () => {
        sendToMainMachine({ type: 'SET_ONE_TIME_PAYMENT_FLOW' });
    };
    const handleCloseButton = () => {
        if (additionalProps.flow.type === MAPFlowTypes.MULTI_PAYMENT_FLOW &&
            featureFlags.isDigitalCollectionsEnabled) {
            handleClose();
        }
        else {
            sendToMainMachine({ type: 'CANCEL' });
        }
    };
    useEffect(() => {
        const eventOptionsV1 = submitSuccessAnalyticsHandler(oneTimePaymentActor.context);
        const eventOptionsV2 = createConfirmationEventObject();
        triggerAnalyticsEvent(eventOptionsV1, eventOptionsV2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(PaymentStepper, { step: "confirm" }), _jsxs(OneTimePaymentConfirmationDetailsWrapper, Object.assign({ bottom: "large", top: "xlarge", vertical: "large" }, { children: [_jsx(OneTimePaymentConfirmationDetails, { autopayConfirmationBannerURL: autopayConfirmationImg, availableCredit: availableCredit, billing: billing, creditLimit: creditLimit, featureFlags: featureFlags, hasOneClickAutopayError: hasOneClickAutopayError, initialAvailableCredit: initialAvailableCredit, isPaymentPending: isPaymentPending, payFrom: payFrom, paymentAmount: paymentAmount, paymentId: paymentId, paymentMedium: paymentMedium, paymentOption: paymentOption, productType: productType, scheduledPaymentDate: scheduledPaymentDate, shouldDisplayAvailableCredit: shouldDisplayAvailableCredit, shouldHaveEnrolledInOneClickAutopay: shouldHaveEnrolledInOneClickAutopay, staticHostName: staticHostName }), shouldHaveEnrolledInOneClickAutopay && (_jsx(OneClickAutopay, { hasOneClickAutopayError: hasOneClickAutopayError, nextDueDate: nextDueDate, payFrom: payFrom, paymentAmount: paymentAmount, paymentOption: paymentOption }))] })), _jsx(FooterContainer, { children: shouldShowMakeAnotherPaymentButton ? (_jsx(ModalFooter, { "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, dataCy: "one-time-payment-confirmation-footer", dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: "make another payment", dataPrimaryType: DATA_TYPE.MAKE_A_PAYMENT, dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: getPrimaryReason(shouldHaveEnrolledInOneClickAutopay, isPaymentPending, hasOneClickAutopayError), dataSecondaryType: "make a payment", handlePrimaryButton: handleMakeAnotherPayment, handleSecondaryButton: handleCloseButton, primaryText: t('button.makeAnotherPayment'), secondaryButtonType: "ghost", secondaryText: t('button.close') })) : (_jsx(ModalFooter, { "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, dataCy: "one-time-payment-confirmation-footer", dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: getPrimaryReason(shouldHaveEnrolledInOneClickAutopay, isPaymentPending, hasOneClickAutopayError), dataPrimaryType: "make a payment", handlePrimaryButton: handleCloseButton, primaryText: t('button.close') })) })] }));
};
export default OneTimePaymentConfirmation;
